import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plane, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LanguageSelector } from "@/components/common/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErpThemeProvider, useErpTheme, assetUrl } from "@/context/ErpThemeContext";
import { Link } from "react-router-dom";
import { TwoFactorForm } from "@/components/auth/TwoFactorForm";
import { useST } from "@/i18n/securityText";

export const PortalLogin = () => {
  const { t } = useTranslation();
  const { login, loginWithToken, isAuthenticated, loading } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const from = location.state?.from?.pathname || "/portal";

  useEffect(() => {
    document.title = "VENKA ERP";
  }, []);

  if (!loading && isAuthenticated) {
    return <Navigate to="/portal" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const r = await login(email, password);
      if (r?.twoFactor) { setChallenge(r.challenge); return; }
      navigate(from, { replace: true });
    } catch (err) {
      setError(t("portal.login.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ErpThemeProvider>
      <LoginScreen t={t} email={email} setEmail={setEmail} password={password} setPassword={setPassword}
        error={error} submitting={submitting} onSubmit={handleSubmit}
        twoFactor={challenge && <TwoFactorForm challenge={challenge} onBack={() => { setChallenge(null); setPassword(""); }}
          onDone={(tok, u) => { loginWithToken(tok, u); navigate(from, { replace: true }); }} />} />
    </ErpThemeProvider>
  );
};

const LoginScreen = ({ t, email, setEmail, password, setPassword, error, submitting, onSubmit, twoFactor }) => {
  const { branding } = useErpTheme();
  const { st } = useST();
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);
  const tagline = branding.login_tagline?.[lang] || branding.login_tagline?.en;
  const logo = assetUrl(branding.logo_url) || "/venka-logo.png";
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-background">
      <section className="erp-dark-zone erp-sidebar relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden">
        <div className="erp-logo-tile w-fit"><img src={logo} alt={branding.app_name} className="h-14 w-auto" /></div>
        <div className="max-w-md">
          <p className="text-xs uppercase tracking-[0.25em] text-venka-green font-semibold mb-4">{branding.erp_title}</p>
          <h1 className="text-4xl font-bold leading-tight">{branding.app_name}</h1>
          {tagline && <p className="mt-4 text-lg text-slate-300">{tagline}</p>}
        </div>
        <div className="flex gap-2">
          <span className="h-1.5 w-16 rounded-full bg-venka-blue" /><span className="h-1.5 w-6 rounded-full bg-venka-green" />
        </div>
        <Plane className="absolute -right-10 top-1/3 h-72 w-72 text-white/[0.04] rotate-12" />
      </section>
      <section className="flex flex-col">
        <div className="flex justify-between items-center p-5">
          <img src={logo} alt="" className="lg:hidden h-10 w-auto" />
          <div className="ml-auto"><LanguageSelector /></div>
        </div>
        <div className="flex-1 grid place-items-center px-6 pb-16">
          <div className="w-full max-w-sm">
            <h2 className="text-2xl font-bold text-slate-100">{t("portal.login.title")}</h2>
            <p className="text-sm text-slate-400 mt-1 mb-8">{t("portal.login.subtitle")}</p>
            {twoFactor || (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="portal-login-form">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-slate-300">{t("portal.login.email")}</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                  className="h-11 bg-slate-950 border-slate-700" data-testid="portal-login-email" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-slate-300">{t("portal.login.password")}</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
                  className="h-11 bg-slate-950 border-slate-700" data-testid="portal-login-password" />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="portal-login-error">{error}</p>}
              <Button type="submit" disabled={submitting} className="w-full h-11 rounded-xl bg-venka-blue text-white hover:bg-venka-blue-dark font-semibold" data-testid="portal-login-submit">
                {submitting ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {t("portal.login.signingIn")}</span> : t("portal.login.submit")}
              </Button>
              <p className="text-center"><Link to="/forgot-password?from=portal" className="text-sm text-venka-blue hover:underline" data-testid="forgot-link">{st("forgot")}</Link></p>
            </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PortalLogin;
